import React from 'react';
import type { Modifier } from 'react-day-picker';

import { multiDatePickerModifiers } from './modifiers';
import type { Errors, IntervalChip } from './MultiDatePicker';
import { type InputFocus, MultiDatePickerNavbar } from './MultiDatePickerNavbar';
import moment, { type Moment } from 'moment';

import { Chip } from '../../Chip/Chip';
import { Divider } from '../../Divider';
import { BasePicker } from '../BasePicker';
import { chipsStyles, multiDatePicker } from '../util/styles';

export interface MultiDatePickerBodyProps {
  disabledDays?: Modifier;
  startDate?: Moment;
  endDate?: Moment;
  hoveredDate?: Moment;
  month?: Moment;
  focus: InputFocus;
  startDateInputValue: string;
  endDateInputValue: string;
  showErrorMessage?: boolean;
  errors?: Errors;

  className?: string;
  intervalChips?: IntervalChip[];

  handleDayMouseLeave: () => void;
  handleDayMouseDown: (day: Moment) => void;
  handleDayMouseEnter: (day: Moment) => void;
  handleMonthChange?: (month: Moment) => void;

  handleInputFocus: (focus: InputFocus) => void;
  handleInputChange: (inputValue: string) => void;

  handleChipClick?: (chip: IntervalChip) => void;

  setStartInput?: (input?: HTMLElement) => void;
  setEndInput?: (input?: HTMLElement) => void;
}

/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
export class MultiDatePickerBody extends React.Component<MultiDatePickerBodyProps> {
  private handleDayMouseEnter = (dayDate: Date) => {
    const day = moment(dayDate).hour(0);
    this.props.handleDayMouseEnter(day);
  };

  private handleDayMouseDown = (dayDate: Date) => {
    const day = moment(dayDate).hour(0);
    this.props.handleDayMouseDown(day);
  };

  private handleMonthChange = (monthDate: Date) => {
    if (!this.props.handleMonthChange) {
      return;
    }
    const month = moment(monthDate).hour(0);
    this.props.handleMonthChange(month);
  };

  private handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    this.props.handleInputChange(value);
  };

  private handleInputFocus = (focus: InputFocus) => {
    this.props.handleInputFocus(focus);
  };

  private renderChips = () => {
    const { startDateInputValue, endDateInputValue, handleChipClick } = this.props;

    if (!handleChipClick) {
      return;
    }

    return (
      <>
        <Divider className={chipsStyles.divider} />
        <div className={chipsStyles.chipContainer}>
          {this.props.intervalChips?.map((chip, idx) => {
            const isEquivalentPeriod =
              moment(startDateInputValue, 'DD MM YYYY').isSame(chip.startDate, 'day') && moment(endDateInputValue, 'DD MM YYYY').isSame(chip.endDate, 'day');

            return (
              <Chip
                key={idx}
                label={chip.label}
                active={isEquivalentPeriod}
                onClick={() => handleChipClick(chip)}
                data-test={`interval-chip-${chip.label.replace(/ /g, '-').toLowerCase()}`}
              />
            );
          })}
        </div>
      </>
    );
  };

  public render = () => {
    const modifiers = multiDatePickerModifiers.defineModifiers(this.props);
    const selectedDays = multiDatePickerModifiers.defineSelectedDays(this.props);
    const disabledDays = multiDatePickerModifiers.defineDisabledDays(this.props);
    const monthToShow = this.props.month ? this.props.month.toDate() : undefined;

    return (
      <div className={`${multiDatePicker.wrapper} ${this.props.className || ''}`}>
        <MultiDatePickerNavbar
          setStartInput={this.props.setStartInput}
          setEndInput={this.props.setEndInput}
          startDateInputValue={this.props.startDateInputValue}
          endDateInputValue={this.props.endDateInputValue}
          handleInputFocus={this.handleInputFocus}
          handleInputChange={this.handleInputChange}
          errors={this.props.errors}
        />
        <BasePicker
          canChangeMonth
          numberOfMonths={2}
          selectedDays={selectedDays}
          modifiers={modifiers}
          disabledDays={disabledDays}
          onDayMouseDown={this.handleDayMouseDown}
          onDayMouseEnter={this.handleDayMouseEnter}
          onDayMouseLeave={this.props.handleDayMouseLeave}
          modifiersStyles={multiDatePicker.modifierStyles}
          classNames={multiDatePicker.classNames}
          month={monthToShow}
          onMonthChange={this.handleMonthChange}
        />
        {this.props.handleChipClick && this.renderChips()}
      </div>
    );
  };
}
